import { useEffect, useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { fetchEconomyOverview, fetchUserStatement, adjustWallet } from '../../../api/walletApi';
import { useToast, ToastContainer } from '../../../components/Toast/Toast';

interface WalletOverview {
  userId: string;
  characterName: string;
  email: string;
  balance: number;
  lastUpdated: string;
}

interface TransactionEntry {
  id: string;
  type: string;
  amount: number;
  source: string;
  description: string | null;
  balanceBefore: number;
  balanceAfter: number;
  createdAt: string;
}

interface AuditInfo {
  walletBalance: number;
  calculatedBalance: number;
  difference: number;
  isConsistent: boolean;
  totalTransactions: number;
}

export function EconomyPage() {
  const { t } = useTranslation();
  const { messages, toast, remove } = useToast();

  const [loading, setLoading] = useState(true);
  const [totalBalance, setTotalBalance] = useState(0);
  const [walletCount, setWalletCount] = useState(0);
  const [totalTx, setTotalTx] = useState(0);
  const [wallets, setWallets] = useState<WalletOverview[]>([]);
  const [search, setSearch] = useState('');

  // Statement view
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState('');
  const [transactions, setTransactions] = useState<TransactionEntry[]>([]);
  const [audit, setAudit] = useState<AuditInfo | null>(null);
  const [stmtLoading, setStmtLoading] = useState(false);
  const [userBalance, setUserBalance] = useState(0);

  // Adjust form
  const [adjustAmount, setAdjustAmount] = useState(0);
  const [adjustType, setAdjustType] = useState<'CREDIT' | 'DEBIT'>('CREDIT');
  const [adjustDesc, setAdjustDesc] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  useEffect(() => { loadOverview(); }, []);

  const loadOverview = async () => {
    try {
      setLoading(true);
      const data = await fetchEconomyOverview();
      setTotalBalance(data.totalBalance);
      setWalletCount(data.walletCount);
      setTotalTx(data.totalTransactions);
      setWallets(data.wallets);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStatement = async (userId: string, name: string) => {
    try {
      setStmtLoading(true);
      setSelectedUser(userId);
      setSelectedName(name);
      const data = await fetchUserStatement(userId, 100);
      setTransactions(data.transactions);
      setAudit(data.audit);
      setUserBalance(data.wallet?.balance ?? 0);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setStmtLoading(false);
    }
  };

  const handleAdjust = async () => {
    if (!selectedUser || adjustAmount <= 0) return;
    try {
      setAdjusting(true);
      const result = await adjustWallet(selectedUser, adjustAmount, adjustType, adjustDesc || 'Ajuste administrativo');
      toast.success(result.message);
      setAdjustAmount(0);
      setAdjustDesc('');
      await loadStatement(selectedUser, selectedName);
      await loadOverview();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setAdjusting(false);
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const sourceLabel = (src: string) => {
    const key = `economy.src.${src}` as any;
    const translated = t(key);
    return translated !== key ? translated : src;
  };

  // ── Styles ──────────────────────────────────────────────────────────────────
  const card: React.CSSProperties = {
    background: 'rgba(22, 36, 22, 0.8)',
    border: '1px solid rgba(42, 74, 42, 0.6)',
    borderRadius: 10,
    padding: '20px 24px',
  };

  const statCard: React.CSSProperties = {
    ...card,
    textAlign: 'center',
    flex: 1,
    minWidth: 150,
  };

  const statValue: React.CSSProperties = {
    fontSize: 28,
    fontWeight: 900,
    color: '#44cc88',
    margin: '8px 0 4px',
  };

  const statLabel: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    color: '#7aaa7a',
  };

  if (loading) return <div style={{ padding: 24, color: '#7aaa7a' }}>{t('common.loading')}</div>;

  // ── Statement View ──────────────────────────────────────────────────────────
  if (selectedUser) {
    return (
      <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
        <button
          onClick={() => setSelectedUser(null)}
          style={{
            background: 'none', border: 'none', color: '#44cc88', cursor: 'pointer',
            fontSize: 13, fontWeight: 700, marginBottom: 16, padding: 0,
          }}
        >
          {t('economy.backToOverview' as any)}
        </button>

        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h3 style={{ margin: 0, color: '#c8e6c8', fontSize: 18 }}>
                📄 {t('economy.statement' as any)} — {selectedName}
              </h3>
              <p style={{ margin: '4px 0 0', color: '#7aaa7a', fontSize: 12 }}>
                {t('economy.balance' as any)}: <strong style={{ color: '#44cc88', fontSize: 16 }}>{userBalance.toLocaleString()} TF</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Audit Banner */}
        {audit && (
          <div style={{
            ...card,
            marginBottom: 16,
            borderColor: audit.isConsistent ? 'rgba(68, 204, 136, 0.4)' : 'rgba(255, 80, 80, 0.6)',
            background: audit.isConsistent ? 'rgba(68, 204, 136, 0.05)' : 'rgba(255, 80, 80, 0.08)',
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: audit.isConsistent ? '#44cc88' : '#ff6666' }}>
              {audit.isConsistent
                ? t('economy.auditOk' as any)
                : `${t('economy.auditFail' as any)} ${audit.difference} TF`
              }
            </div>
            <div style={{ display: 'flex', gap: 24, marginTop: 8, fontSize: 12, color: '#7aaa7a' }}>
              <span>{t('economy.walletBalance' as any)}: <strong>{audit.walletBalance}</strong></span>
              <span>{t('economy.calculatedBalance' as any)}: <strong>{audit.calculatedBalance}</strong></span>
              <span>{t('economy.totalTransactions' as any)}: <strong>{audit.totalTransactions}</strong></span>
            </div>
          </div>
        )}

        {/* Adjust Form */}
        <div style={{ ...card, marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 12px', color: '#c8a040', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            🔧 {t('economy.adjust' as any)}
          </h4>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: '#7aaa7a' }}>
              {t('economy.adjustType' as any)}
              <select
                value={adjustType}
                onChange={e => setAdjustType(e.target.value as 'CREDIT' | 'DEBIT')}
                style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(42,74,42,0.6)', color: '#c8e6c8', padding: '6px 10px', borderRadius: 6, fontSize: 13 }}
              >
                <option value="CREDIT">{t('economy.credit' as any)}</option>
                <option value="DEBIT">{t('economy.debit' as any)}</option>
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: '#7aaa7a' }}>
              {t('economy.adjustAmount' as any)}
              <input
                type="number" min="1" value={adjustAmount || ''}
                onChange={e => setAdjustAmount(parseInt(e.target.value) || 0)}
                style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(42,74,42,0.6)', color: '#c8e6c8', padding: '6px 10px', borderRadius: 6, fontSize: 13, width: 100 }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: '#7aaa7a', flex: 1, minWidth: 150 }}>
              {t('economy.adjustDesc' as any)}
              <input
                type="text" value={adjustDesc}
                onChange={e => setAdjustDesc(e.target.value)}
                placeholder="Ex: Evento especial, Correção..."
                style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(42,74,42,0.6)', color: '#c8e6c8', padding: '6px 10px', borderRadius: 6, fontSize: 13 }}
              />
            </label>
            <button
              onClick={handleAdjust}
              disabled={adjusting || adjustAmount <= 0}
              style={{
                background: adjustType === 'CREDIT' ? '#44cc88' : '#cc4444',
                color: '#fff', border: 'none', padding: '8px 18px', borderRadius: 6,
                fontWeight: 700, fontSize: 12, cursor: adjustAmount > 0 ? 'pointer' : 'not-allowed',
                opacity: adjustAmount > 0 ? 1 : 0.4,
              }}
            >
              {adjusting ? '...' : t('economy.adjustSubmit' as any)}
            </button>
          </div>
        </div>

        {/* Transactions Table */}
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          {stmtLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#7aaa7a' }}>{t('common.loading')}</div>
          ) : transactions.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#7aaa7a' }}>{t('economy.noTransactions' as any)}</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'rgba(0,0,0,0.2)' }}>
                    {[t('economy.date' as any), t('economy.type' as any), t('economy.amount' as any), t('economy.source' as any), t('economy.description' as any), t('economy.balanceBefore' as any), t('economy.balanceAfter' as any)].map((h, i) => (
                      <th key={i} style={{ padding: '10px 12px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#7aaa7a', textAlign: 'left', borderBottom: '1px solid rgba(42,74,42,0.6)' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(tx => (
                    <tr key={tx.id} style={{ borderBottom: '1px solid rgba(42,74,42,0.3)' }}>
                      <td style={{ padding: '10px 12px', fontSize: 12, color: '#c8e6c8' }}>{formatDate(tx.createdAt)}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                          background: tx.type === 'CREDIT' ? 'rgba(68,204,136,0.15)' : 'rgba(255,80,80,0.15)',
                          color: tx.type === 'CREDIT' ? '#44cc88' : '#ff6666',
                          border: `1px solid ${tx.type === 'CREDIT' ? 'rgba(68,204,136,0.3)' : 'rgba(255,80,80,0.3)'}`,
                        }}>
                          {tx.type === 'CREDIT' ? '▲' : '▼'} {tx.type === 'CREDIT' ? t('economy.credit' as any) : t('economy.debit' as any)}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 14, fontWeight: 800, color: tx.type === 'CREDIT' ? '#44cc88' : '#ff6666' }}>
                        {tx.type === 'CREDIT' ? '+' : '-'}{tx.amount.toLocaleString()}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 11, color: '#c8e6c8' }}>{sourceLabel(tx.source)}</td>
                      <td style={{ padding: '10px 12px', fontSize: 11, color: '#7aaa7a' }}>{tx.description || '—'}</td>
                      <td style={{ padding: '10px 12px', fontSize: 11, color: '#7aaa7a' }}>{tx.balanceBefore.toLocaleString()}</td>
                      <td style={{ padding: '10px 12px', fontSize: 11, color: '#c8e6c8', fontWeight: 700 }}>{tx.balanceAfter.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <ToastContainer messages={messages} onRemove={remove} />
      </div>
    );
  }

  // ── Overview ────────────────────────────────────────────────────────────────
  
  // Create an array with rank before filtering
  const walletsWithRank = wallets.map((w, i) => ({ ...w, rank: i + 1 }));
  
  // Filter logic
  const filteredWallets = walletsWithRank.filter(w => {
    const q = search.toLowerCase();
    if (!q) return true;
    
    // Check rank
    if (w.rank.toString() === q || `#${w.rank}` === q) return true;
    
    // Check name
    if (w.characterName.toLowerCase().includes(q)) return true;
    
    // Check email
    if (w.email.toLowerCase().includes(q)) return true;
    
    return false;
  });

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      <header style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
        <h2 style={{ margin: 0, color: '#c8e6c8', fontSize: 18 }}>💰 {t('economy.title' as any)}</h2>
        <input 
          type="text" 
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome, email ou posição..." 
          style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(42,74,42,0.6)', color: '#c8e6c8', padding: '8px 14px', borderRadius: 6, fontSize: 13, minWidth: 250 }}
        />
      </header>

      {/* Stats Cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={statCard}>
          <div style={statLabel}>{t('economy.totalBalance' as any)}</div>
          <div style={statValue}>{totalBalance.toLocaleString()}</div>
          <div style={{ fontSize: 10, color: '#7aaa7a' }}>Ta Feito</div>
        </div>
        <div style={statCard}>
          <div style={statLabel}>{t('economy.walletCount' as any)}</div>
          <div style={{ ...statValue, color: '#64b4ff' }}>{walletCount}</div>
        </div>
        <div style={statCard}>
          <div style={statLabel}>{t('economy.totalTransactions' as any)}</div>
          <div style={{ ...statValue, color: '#c8a040' }}>{totalTx}</div>
        </div>
      </div>

      {/* Wallets Table */}
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        {wallets.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#7aaa7a' }}>{t('economy.noWallets' as any)}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(0,0,0,0.2)' }}>
                {['#', t('economy.player' as any), 'Email', t('economy.balance' as any), t('economy.lastUpdate' as any), ''].map((h, i) => (
                  <th key={i} style={{ padding: '10px 16px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#7aaa7a', textAlign: 'left', borderBottom: '1px solid rgba(42,74,42,0.6)' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredWallets.map(w => (
                <tr key={w.userId} style={{ borderBottom: '1px solid rgba(42,74,42,0.3)' }}>
                  <td style={{ padding: '12px 16px', color: '#c8a040', fontSize: 14, fontWeight: 800 }}>#{w.rank}</td>
                  <td style={{ padding: '12px 16px', color: '#c8e6c8', fontSize: 14, fontWeight: 600 }}>{w.characterName}</td>
                  <td style={{ padding: '12px 16px', color: '#7aaa7a', fontSize: 12 }}>{w.email}</td>
                  <td style={{ padding: '12px 16px', color: '#44cc88', fontSize: 16, fontWeight: 800 }}>{w.balance.toLocaleString()} TF</td>
                  <td style={{ padding: '12px 16px', color: '#7aaa7a', fontSize: 12 }}>{formatDate(w.lastUpdated)}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <button
                      onClick={() => loadStatement(w.userId, w.characterName)}
                      style={{
                        background: 'rgba(68,204,136,0.1)', border: '1px solid rgba(68,204,136,0.3)',
                        color: '#44cc88', padding: '5px 14px', borderRadius: 6, cursor: 'pointer',
                        fontSize: 12, fontWeight: 700, transition: 'all 0.15s',
                      }}
                    >
                      📄 {t('economy.viewStatement' as any)}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ToastContainer messages={messages} onRemove={remove} />
    </div>
  );
}
